import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useApp } from '@/components/hooks/useApp';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { safeStorage } from '@/lib/safeStorage';

// User preference > tenant default (ADR-0004 · decision E).
// Tenant ships company.theme.density as the recommended starting point;
// the user can override via the topbar menu and the choice sticks across
// sessions AND devices: the source of truth is the per-user backend preference
// (users.preferences.density), with localStorage as a synchronous cache so the
// chosen density applies on first paint without waiting for the network.

const STORAGE_KEY = 'verix_density';
const VALID = new Set(['compact', 'cozy', 'spacious']);
const DEFAULT_DENSITY = 'cozy';

function readUserPref() {
  const raw = safeStorage.getItem(STORAGE_KEY);
  return VALID.has(raw) ? raw : null;
}

export function useDensity() {
  const { company } = useApp();
  const { preferences, update: updatePrefs } = useUserPreferences();
  const tenantDefault = VALID.has(company?.theme?.density)
    ? company.theme.density
    : DEFAULT_DENSITY;

  const [userPref, setUserPref] = useState(() => readUserPref());

  // Cross-device hydration: the backend preference wins over a possibly stale
  // local cache (e.g. set on another device). Mirror it into localStorage so
  // subsequent first paints are instant.
  const backendDensity = VALID.has(preferences?.density) ? preferences.density : null;
  useEffect(() => {
    if (backendDensity && backendDensity !== userPref) {
      safeStorage.setItem(STORAGE_KEY, backendDensity);
      setUserPref(backendDensity);
    }
  }, [backendDensity, userPref]);

  // Ensure tenant changes don't drop a stale user pref — user wins always.
  const density = userPref || tenantDefault;

  // Reflect the active density on <html data-density> so any descendant CSS
  // can branch on it (e.g. table row heights via [data-density="compact"]).
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  const setDensity = useCallback((next) => {
    if (!VALID.has(next)) return;
    safeStorage.setItem(STORAGE_KEY, next);
    setUserPref(next);
    updatePrefs({ density: next }); // write-through to the cross-device source of truth
  }, [updatePrefs]);

  const clearOverride = useCallback(() => {
    safeStorage.removeItem(STORAGE_KEY);
    setUserPref(null);
    // Push the tenant default to the backend so the cross-device hydration
    // doesn't re-apply the previous override on the next render.
    updatePrefs({ density: tenantDefault });
  }, [updatePrefs, tenantDefault]);

  return {
    density,
    setDensity,
    clearOverride,
    isOverridden: userPref !== null && userPref !== tenantDefault,
    tenantDefault,
  };
}

export const __test = { STORAGE_KEY, VALID, DEFAULT_DENSITY };
